import { useEffect, useState } from "react";
import { SYSTEMS, SYSTEM_ID } from "../integration";
import { IconClose } from "../icons";

/** Aviso emitido pelo Admin Cybersecurity (Portal) para os usuários deste sistema. */
interface Aviso {
  id: number;
  tipo: "falha" | "manutencao" | "comunicado";
  titulo: string;
  mensagem: string;
  sistema_alvo: string | null;
  criado_em: string;
  expira_em: string | null;
}

const DISMISSED_KEY = "sercamp-avisos-dispensados";
const POLL_MS = 60_000;

function getDismissed(): number[] {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function addDismissed(id: number) {
  const atual = getDismissed();
  if (!atual.includes(id)) {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...atual, id]));
  }
}

const ESTILO_POR_TIPO: Record<Aviso["tipo"], { cor: string; corSoft: string; rotulo: string; icone: string }> = {
  falha: { cor: "var(--danger)", corSoft: "var(--danger-soft)", rotulo: "Falha", icone: "⛔" },
  manutencao: { cor: "var(--warn)", corSoft: "var(--warn-soft)", rotulo: "Manutenção", icone: "🛠️" },
  comunicado: { cor: "var(--brand)", corSoft: "var(--badge-bg)", rotulo: "Aviso", icone: "📣" },
};

/**
 * Banner de avisos globais do Portal (falha / manutenção / comunicado), publicado
 * pelo Admin Cybersecurity. Busca no Portal (endpoint público, sem autenticação) e
 * permite dispensar cada aviso (fica salvo no localStorage deste sistema).
 */
export default function AvisoSistema() {
  const [avisos, setAvisos] = useState<Aviso[]>([]);

  useEffect(() => {
    let vivo = true;

    async function carregar() {
      try {
        const res = await fetch(
          `${SYSTEMS.portal}/api/security/notificacoes/ativas?sistema=${encodeURIComponent(SYSTEM_ID)}`,
        );
        if (!res.ok) return;
        const dados = (await res.json()) as Aviso[];
        if (!vivo || !Array.isArray(dados)) return;
        const dispensados = getDismissed();
        setAvisos(dados.filter((a) => !dispensados.includes(a.id)));
      } catch {
        /* Portal fora do ar: não quebra este sistema, apenas não mostra avisos. */
      }
    }

    carregar();
    const id = setInterval(carregar, POLL_MS);
    return () => {
      vivo = false;
      clearInterval(id);
    };
  }, []);

  function dispensar(id: number) {
    addDismissed(id);
    setAvisos((atual) => atual.filter((a) => a.id !== id));
  }

  if (avisos.length === 0) return null;

  return (
    <div className="aviso-sistema-stack">
      {avisos.map((a) => {
        const estilo = ESTILO_POR_TIPO[a.tipo] ?? ESTILO_POR_TIPO.comunicado;
        return (
          <div
            key={a.id}
            className="aviso-sistema"
            style={{ borderColor: estilo.cor, background: estilo.corSoft }}
          >
            <span className="aviso-sistema-icone" aria-hidden="true">{estilo.icone}</span>
            <div className="aviso-sistema-texto">
              <strong style={{ color: estilo.cor }}>{a.titulo}</strong>
              <span>{a.mensagem}</span>
            </div>
            <button
              className="aviso-sistema-fechar"
              onClick={() => dispensar(a.id)}
              title="Dispensar"
              aria-label="Dispensar aviso"
            >
              <IconClose size={15} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
